import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'emotion-theming';

import AcceptAgreementField from '../shared/AcceptAgreementField';
import Button from '../../../../shared/_TBComponents/Button';
import CommonFields from '../shared/CommonFields';
import GoogleAuthSection from '../shared/GoogleAuthSection';
import RegistrationFormWrapper from '../shared/RegistrationFormWrapper';
import { RegistrationSchemaPrimAdmin, RegistrationSchemaGooglePrimAdmin } from '../../../../shared/utils/registrationSchemaHelper';
import {
  cancelRegistration,
  registerUser,
} from '../../ducks/registrationActions';

const FormGroup = styled('div')`  
  label {
    width: 100%;
  }

  @media screen and (max-width: 425px) {
    margin-bottom: 10px;
  }
`;

const ExistingPrimaryAdminRegistrationForm = () => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const googleUser = useSelector((state) => state.registration.data.googleUser);
  const isRegisteringWithGoogle = useSelector(
    (state) => state.registration.data.isRegisteringWithGoogle,
  );
  const isSubmitting = useSelector((state) => state.registration.ui.isSubmitting);

  const dispatch = useDispatch();
  const theme = useTheme();

  const [googleResponse, setGoogleResponse] = useState(null);

  const getInitialValues = () => {
    let emailAddress = '';
    let firstName = '';
    let lastName = '';

    if (isRegisteringWithGoogle) {
      emailAddress = googleUser.email || '';
      firstName = googleUser.givenName || '';
      lastName = googleUser.familyName || '';
    } else if (currentUser?.id) {
      emailAddress = currentUser.email || '';
      firstName = currentUser.first || '';
      lastName = currentUser.last || '';
    }

    return {
      acceptAgreement: false,
      countryCode: currentUser.phoneCountryCode || undefined,
      emailAddress,
      firstName,
      gender: currentUser.gender || undefined,
      lastName,
      organizationName: currentUser.organizationName || '',
      password: '',
      phone: currentUser.phone || undefined,
    };
  };

  const handleCancel = () => {
    dispatch(cancelRegistration(!!currentUser.id));
  };

  const handleGoogleConnect = (response) => {
    setGoogleResponse(response);
  };

  const handleRegistration = (values) => {
    let submissionObject = {};

    if (isRegisteringWithGoogle) {
      submissionObject = {
        acceptAgreement: values.acceptAgreement,
        acceptTeamAgreement: values.acceptTeamAgreement,
        accountCode: currentUser.accountCode,
        authenticationMethod: 'GOOGLE',
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        countryCode: values.countryCode,
        emailAddress: values.emailAddress,
        firstName: values.firstName,
        googleToken: googleResponse.tokenId,
        lastName: values.lastName,
        organizationName: values.organizationName,
        mobileNumber: values.phone,
        registrationType: 'COMPLETE_REGISTRATION',
        userId: currentUser.id,
      };
    } else {
      submissionObject = {
        acceptAgreement: values.acceptAgreement,
        acceptTeamAgreement: values.acceptTeamAgreement,
        accountCode: currentUser.accountCode,
        authenticationMethod: 'PASSWORD',
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        countryCode: values.countryCode,
        emailAddress: values.emailAddress,
        firstName: values.firstName,
        lastName: values.lastName,
        organizationName: values.organizationName,
        password: values.password,
        mobileNumber: values.phone,
        registrationType: 'COMPLETE_REGISTRATION',
        userId: currentUser.id,
      };
    }

    dispatch(registerUser(submissionObject));
  };

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues()}
      onSubmit={(values) => {
        handleRegistration(values);
      }}
      validationSchema={
        isRegisteringWithGoogle
          ? RegistrationSchemaGooglePrimAdmin
          : RegistrationSchemaPrimAdmin
      }
      render={({
        errors,
        handleSubmit,
        setFieldValue,
        touched,
        values,
      }) => (
        <RegistrationFormWrapper
          subtitle='Enter the necessary details below to complete your registration.'
          title='Registration'
        >
          <Form
            id='registration'
            name='registration'
          >
            <GoogleAuthSection
              onGoogleConnect={handleGoogleConnect}
            />
            <CommonFields
              errors={errors}
              setFieldValue={setFieldValue}
              showGenderField={false}
              showOrgFields
              showPasswordFields={!isRegisteringWithGoogle}
              touched={touched}
              values={values}
            />
            <AcceptAgreementField
              error={errors.acceptAgreement}
              setFieldValue={setFieldValue}
              showAgeNotice={false}
              touched={touched.acceptAgreement}
              values={{
                acceptAgreement: values.acceptAgreement,
              }}
            />
            <FormGroup>
              <Button
                bgColor={theme.colors.orange}
                disabled={errors.acceptAgreement
                    || errors.confirmedPassword
                    || errors.emailAddress
                    || errors.password
                    || errors.phone
                    || errors.countryCode
                    || isSubmitting}
                fontSize='14px'
                fullWidth
                id='submit'
                large
                name='submit'
                noBorder
                onClick={handleSubmit}
                rounded
                txtColor={theme.colors.white}
                type='submit'
                upperCase
              />
              <Button
                fontSize='14px'
                fullWidth
                large
                name='Return to login'
                noBorder
                onClick={handleCancel}
                styledWrapperProps={{ marginTop: '15px' }}
                txtColor={theme.colors.lightText[1]}
                type='button'
                upperCase
              />
            </FormGroup>
          </Form>
        </RegistrationFormWrapper>
      )}
    />
  );
};

export default ExistingPrimaryAdminRegistrationForm;
